import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout, { usePageTitle } from '../components/site/Layout';
import VehicleSearch from '../components/site/VehicleSearch';
import VehicleReport from '../components/site/VehicleReport';
import Quiz from '../components/site/Quiz';
import ProviderGuide from '../components/site/ProviderGuide';
import { loadVehicle } from '../lib/nhtsa';
import { FAQS } from '../lib/providers';

const CRITERIA = [
  ['Where the data comes from', 'How many independent sources feed the report, and whether it includes the federal NMVTIS layer for title brands, salvage and theft.'],
  ['What it costs per car', 'Price for one report and at realistic volumes — a $25 report is cheap for one car and expensive for twenty.'],
  ['What it can and cannot show', 'Service records, auction history, photos, imports. We say plainly what each provider is not good at.'],
  ['Refunds and policies', 'Buyback or guarantee terms, subscription renewal rules, and how easy it is to cancel.'],
];

export default function Landing() {
  usePageTitle('CompareVIN — Free VIN & Model Check, Plus a Guide to Vehicle History Reports');
  const [state, setState] = useState({ status: 'idle', data: null, error: '' });
  const reqId = useRef(0);

  const run = async (input) => {
    const id = ++reqId.current;
    setState({ status: 'loading', data: null, error: '' });
    try {
      const data = await loadVehicle(input);
      if (id !== reqId.current) return;
      setState({ status: 'done', data, error: '' });
      setTimeout(() => document.getElementById('result')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    } catch (e) {
      if (id !== reqId.current) return;
      const known = e && e.message && !/HTTP|Failed to fetch|NetworkError/i.test(e.message);
      setState({ status: 'error', data: null, error: known ? e.message : "Couldn't reach NHTSA just now. Please try again in a moment." });
    }
  };

  return (
    <Layout>
      <section className="cv-hero">
        <div className="cv-wrap">
          <div className="cv-eyebrow">Independent · US market</div>
          <h1>Check any car before you buy it.</h1>
          <p className="cv-lead">Free recall, owner-complaint and crash-test data straight from NHTSA — then an honest guide to which vehicle history report is actually worth paying for.</p>
          <VehicleSearch onSubmit={run} loading={state.status === 'loading'} error={state.error} />
          <div className="cv-trust">
            <span>Official NHTSA data</span>
            <span>No account needed</span>
            <span>We never store your VIN</span>
          </div>
        </div>
      </section>

      {state.status === 'loading' && <div className="cv-wrap cv-loading">Pulling data from NHTSA…</div>}
      {state.status === 'done' && <VehicleReport data={state.data} />}

      <section className="cv-section">
        <div className="cv-wrap">
          <div className="cv-section-head">
            <div className="cv-eyebrow">Free vs. paid</div>
            <h2 style={{ marginTop: 8 }}>What a free check covers — and what it doesn't</h2>
            <p className="cv-lead">Government data is great for safety. It says nothing about a car's past life. Knowing the difference keeps you from overpaying or under-checking.</p>
          </div>
          <div className="cv-split">
            <div className="cv-split-card">
              <h3>Free: NHTSA safety data</h3>
              <ul>
                <li>Recall campaigns for the model year</li>
                <li>Owner complaints, by component</li>
                <li>Official crash-test ratings</li>
                <li>Specs decoded from the VIN</li>
              </ul>
            </div>
            <div className="cv-split-card cv-split-card--paid">
              <h3>Paid: a vehicle history report</h3>
              <ul>
                <li>Accident and damage records</li>
                <li>Title brands: salvage, flood, rebuilt</li>
                <li>Odometer readings and rollback flags</li>
                <li>Ownership count and service history</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="cv-section cv-section--soft" id="quiz">
        <div className="cv-wrap">
          <div className="cv-section-head" style={{ textAlign: 'center', margin: '0 auto 32px' }}>
            <div className="cv-eyebrow">Report finder · 5 questions</div>
            <h2 style={{ marginTop: 8 }}>Which report do you actually need?</h2>
            <p className="cv-lead">Answer five quick questions and we'll tell you which service fits — and which one you don't need to pay for.</p>
          </div>
          <Quiz />
        </div>
      </section>

      <section className="cv-section" id="reports">
        <div className="cv-wrap">
          <div className="cv-section-head">
            <div className="cv-eyebrow">Report guide</div>
            <h2 style={{ marginTop: 8 }}>Our picks for vehicle history reports</h2>
            <p className="cv-lead">Three we'd point most buyers to, then every major provider compared.</p>
          </div>
          <ProviderGuide />
        </div>
      </section>

      <section className="cv-section cv-section--soft">
        <div className="cv-wrap cv-editorial">
          <div>
            <div className="cv-eyebrow">Good to know</div>
            <h2 style={{ margin: '8px 0 18px' }}>Why the same VIN can get two different reports</h2>
            <p>Carfax and AutoCheck don't share one universal database. Each aggregates its own mix of sources — state DMVs, insurers, auctions, repair shops, dealers and law enforcement — so an event reported to one network can be missing from the other.</p>
            <p>NMVTIS, the federal title system, is the common layer: title history, brands like salvage or junk, odometer data and some theft records. It is not a complete history — it doesn't hold every accident or service visit.</p>
            <p>That's why, on a car you're serious about, comparing two reports can reveal what a single one misses.</p>
          </div>
          <div className="cv-editorial-aside">
            <div className="big">≠</div>
            <p>Same VIN, different coverage. Neither report is "wrong" — they simply don't draw from the same sources.</p>
          </div>
        </div>
      </section>

      <section className="cv-section">
        <div className="cv-wrap">
          <div className="cv-section-head">
            <div className="cv-eyebrow">How we choose</div>
            <h2 style={{ marginTop: 8 }}>What we look at</h2>
            <p className="cv-lead">Our guide is editorial: based on each provider's published coverage, pricing and policies. We don't publish invented scores.</p>
          </div>
          <div className="cv-criteria">
            {CRITERIA.map(([title, text]) => (
              <div className="cv-criterion" key={title}><h4>{title}</h4><p>{text}</p></div>
            ))}
          </div>
          <p style={{ marginTop: 20 }}><Link to="/how-we-work">Read our full policy, including how affiliate links work →</Link></p>
        </div>
      </section>

      <section className="cv-section cv-section--soft" id="faq">
        <div className="cv-narrow">
          <h2 style={{ marginBottom: 28 }}>Questions buyers ask</h2>
          <div className="cv-faq">
            {FAQS.map((f, i) => (
              <details key={f.q} open={i === 0}><summary>{f.q}</summary><p>{f.a}</p></details>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
